import {
  Button,
  Input,
  Label,
  Select,
  Toaster,
} from "@fluentui/react-components";
import React, { ChangeEvent, useEffect, useState, useMemo } from "react";
import TextEditor from "../../../components/TextEditor";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import OverlayLoader from "../../../components/OverlayLoader";
import { formatedDate2, getReceiptStatus, getReceiptStatusNumber, stripHtml } from "../../../utils";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import apiServices from "../../../service";
import { updateCommission } from "../../../redux/Commission/commissionSlice";
import DynamicDatePicker from "../../../components/ControlledDatePicker/dynamicDatepicker";
import ASCButton from "../../../components/Button";

const CommissionForm = (props: any) => {
  const { showToast, toasterId } = useToastMessage();
  const dispatch: AppDispatch = useDispatch();

  const data = useSelector((state: RootState) => state.commission.filteredData);
  const loading = useSelector(
    (state: RootState) => state.commission.loading.updateCommission,
  );
  const error = useSelector(
    (state: RootState) => state.commission.error.updateCommission,
  );

  useEffect(() => {
    if (error) {
      showToast(error, "", "error");
    }
  }, [error]);

  const [formField, setFormField] = useState({
    ...data,
    commission_status: String(data.commission_status) || "0",
    commission_startDate: data.commission_startDate || "",
    commission_endDate: data.commission_endDate || "",
    commission_note: data.commission_note || "",
  });
  const [selectFieldOptions, setSelectFieldOptions] = useState<any>([]);

  // Logic to determine if a field is disabled based on Business Rules
  const isFieldDisabled = (fieldName: string) => {
    const status = String(formField.commission_status);
    const category = formField.category; // "Case-related" or "Other Fees/Charges"

    // 1. Status Check: If "In Receipt", only these 3 are ever editable
    const status1Allowed = [
      "commission_type",
      "payment_source",
      "commission_note",
    ];
    if (status === "1") {
      return !status1Allowed.includes(fieldName);
    }

    // 2. Status 0 Check: Awaiting Receipt
    if (status === "0") {
      if (category === "Case-related") {
        const caseFields = [
          "commission_type",
          "payment_source",
          "actual",
          "frequency",
          "commission_startDate",
          "commission_endDate",
          "payment_count",
          "nb_register",
          "commission_note",
        ];
        return !caseFields.includes(fieldName);
      }

      if (category === "Other Fees/Charges") {
        const feeFields = [
          "commission_type",
          "payment_source",
          "actual",
          "frequency",
          "commission_startDate",
          "commission_endDate",
          "payment_count",
          "consultant",
          "commission_note",
        ];
        return !feeFields.includes(fieldName);
      }
    }

    return true; // Default to disabled for safety
  };

  const validateForm = () => {
    const {
      commission_startDate,
      commission_endDate,
      actual,
      commission_type,
      frequency,
      commission_note,
    } = formField;

    // 1. Mandatory Commission Type
    if (!commission_type) return false;

    // 2. Note Length
    if (commission_note && commission_note.length > 5000) return false;

    // 3. Conditional Mandatory Fields (only if editable)
    if (!isFieldDisabled("actual") && (actual === "" || actual === null))
      return false;
    if (
      !isFieldDisabled("frequency") &&
      !frequency &&
      !formField.commission_frequency
    )
      return false;

    // 4. Date Validations
    if (!isFieldDisabled("commission_startDate")) {
      if (!commission_startDate || !commission_endDate) return false;

      const start = moment(commission_startDate, "DD-MM-YYYY");
      const end = moment(commission_endDate, "DD-MM-YYYY");

      if (start.isAfter(end)) return false; // Must be less than or equal to end date
    }

    return true;
  };

  // Check if form has changed to enable Save button
  const isFormDirty = useMemo(() => {
    return (
      JSON.stringify(formField) !==
      JSON.stringify({
        ...data,
        commission_status: String(data.commission_status) || "0",
        commission_startDate: data.commission_startDate || "",
        commission_endDate: data.commission_endDate || "",
        commission_note: data.commission_note || "",
        category: data.category || "Case-related",
      })
    );
  }, [formField, data]);

  const getSelectionListOptions = async () => {
    try {
      const fieldLbl =
        "commission_type:MasterDatawithoutNewEntry,payment_source:FixedList,frequency:FixedList,true_false:FixedList";
      const res = await apiServices.selectionList.getOptions(fieldLbl);
      if (res.data && res.data.data) {
        setSelectFieldOptions(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    getSelectionListOptions();
  }, []);

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormField({ ...formField, [e.target.name]: e.target.value });
  };

  // const handleUpdateCommission = () => {
  //   if (!validateForm()) {
  //     showToast("Please check mandatory fields and date ranges.", "", "error");
  //     return;
  //   }

  //   const formattedFormData: any = {
  //     payment_id: formField.payment_id,
  //     case_id: formField.case_id,
  //     type: formField.type,
  //     customer_id: formField.customer_id,
  //     status: formField.status,
  //     commission_type: formField.commission_type,
  //     payment_source: formField.payment_source,
  //     amount_to_company: formField.actual || "0.00",
  //     commission_frequency: formField.commission_frequency,
  //     commission_startDate: formField.commission_startDate
  //       ? moment(formField.commission_startDate, "DD-MM-YYYY")
  //         .utc()
  //         .format("YYYY-MM-DD")
  //       : null,
  //     commission_endDate: formField.commission_endDate
  //       ? moment(formField.commission_endDate, "DD-MM-YYYY")
  //         .utc()
  //         .format("YYYY-MM-DD")
  //       : null,
  //     payment_count: parseInt(formField.payment_count) || 0,
  //     nb_register: formField.nb_register || 0,
  //     commission_note: formField.commission_note || "",
  //     note_text: formField.commission_note
  //       ? stripHtml(formField.commission_note)
  //       : "",
  //   };

  //   dispatch(
  //     updateCommission({
  //       commissionId: formField.commission_id,
  //       clientId: formField.customer_id,
  //       category: formField.category,
  //       formData: formattedFormData,
  //     }),
  //   ).then((result: any) => {
  //     if (updateCommission.fulfilled.match(result)) {
  //       props?.setSelectedRowIds([]);
  //       props.getReceiptlistData();
  //       showToast(
  //         result.payload?.message || "Commission updated successfully",
  //         "",
  //         "success",
  //       );
  //       setTimeout(() => {
  //         props.setReceiptEditpopup(false);
  //       }, 3000);
  //     }
  //   });
  // };

  const handleUpdateCommission = () => {
    if (!validateForm()) {
      showToast("Please check mandatory fields and date ranges.", "", "error");
      return;
    }

    const formattedFormData: any = {
      payment_id: formField.payment_id,
      case_id: formField.case_id,
      type: formField.type,
      customer_id: formField.customer_id,
      status: String(formField.commission_status),
      commission_id: formField.commission_id,
      commission_module: true,
      commission_startDate: formField.commission_startDate
        ? formatedDate2(formField.commission_startDate)
        : null,
      commission_endDate: formField.commission_endDate
        ? formatedDate2(formField.commission_endDate)
        : null,
      payment_count: formField.payment_count,
    };
    // const currentStatus = String(formField.commission_status);

    // if (currentStatus !== "1") {
    //   formattedFormData.commission_startDate = formField.commission_startDate
    //     ? moment(formField.commission_startDate, "DD-MM-YYYY").utc().format("YYYY-MM-DD")
    //     : null;

    //   formattedFormData.commission_endDate = formField.commission_endDate
    //     ? moment(formField.commission_endDate, "DD-MM-YYYY").utc().format("YYYY-MM-DD")
    //     : null;

    // }
    formattedFormData.commission_frequency = formField.frequency || formField.commission_frequency;

    if (!isFieldDisabled("actual")) {
      formattedFormData.amount_to_company = formField.actual ?? 0;
    }

    if (!isFieldDisabled("commission_type")) {
      formattedFormData.commission_type = formField.commission_type;
    }

    if (!isFieldDisabled("payment_source")) {
      formattedFormData.payment_source = formField.payment_source;
    }

    if (!isFieldDisabled("payment_count")) {
      formattedFormData.payment_count = parseInt(formField.payment_count) || 0;
    }

    if (!isFieldDisabled("nb_register")) {
      formattedFormData.nb_register = formField.nb_register || 0;
    }

    if (!isFieldDisabled("commission_note")) {
      formattedFormData.commission_note = formField.commission_note || "";
      formattedFormData.note_text = formField.commission_note ? stripHtml(formField.commission_note) : "";
    }

    dispatch(
      updateCommission({
        commissionId: formField.commission_id,
        clientId: formField.customer_id,
        category: formField.category,
        formData: formattedFormData,
      })
    ).then((result: any) => {
      if (updateCommission.fulfilled.match(result)) {
        props?.setSelectedRowIds([]);
        props.getReceiptlistData();
        showToast(
          result.payload?.message || "Commission updated successfully",
          "",
          "success",
        );
        setTimeout(() => {
          props.setReceiptEditpopup(false);
        }, 3000);
      }
    });
  };
  return (
    <div>
      <OverlayLoader isLoading={loading} />
      <Toaster
        toasterId={toasterId}
        position="top-end"
        pauseOnHover
        pauseOnWindowBlur
        timeout={1000}
      />
      <div className="commission_action_main">
        <div className="settings-table-content-label">Status</div>
        <span style={{ fontWeight: "bold" }}>
          {getReceiptStatus(formField.commission_status)}
        </span>

        <Label required className="settings-table-content-label">
          Commission Type
        </Label>
        <Select
          name="commission_type"
          value={formField.commission_type || ""}
          onChange={onChange}
          disabled={isFieldDisabled("commission_type")}
        >
          {selectFieldOptions
            .find((option: any) => option.field === "commission_type")
            ?.options.map((opt: any) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
        </Select>

        <Label className="settings-table-content-label">
          Source of Payment
        </Label>
        <Select
          name="payment_source"
          value={formField.payment_source || ""}
          onChange={onChange}
          disabled={isFieldDisabled("payment_source")}
        >
          {selectFieldOptions
            .find((option: any) => option.field === "payment_source")
            ?.options.map((opt: any) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
        </Select>

        <Label
          required={!isFieldDisabled("actual")}
          className="settings-table-content-label"
        >
          Amount to Company
        </Label>
        <Input
          name="actual"
          type="number"
          disabled={isFieldDisabled("actual")}
          value={formField.actual || ""}
          onChange={onChange}
        />

        <Label
          required={!isFieldDisabled("frequency")}
          className="settings-table-content-label"
        >
          Frequency
        </Label>
        <Select
          name="frequency"
          disabled={isFieldDisabled("frequency")}
          value={
            formField.frequency || formField.commission_frequency || "Monthly"
          }
          onChange={onChange}
        >
          {selectFieldOptions
            .find((item: any) => item.field === "frequency")
            ?.options.map((option: any) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
        </Select>

        <Label
          required={!isFieldDisabled("commission_startDate")}
          className="settings-table-content-label"
        >
          Start Date
        </Label>
        <DynamicDatePicker
          border
          name="commission_startDate"
          disabled={isFieldDisabled("commission_startDate")}
          formik={{
            values: {
              commission_startDate: formField.commission_startDate
                ? formField.commission_startDate
                : null,
            },
            setFieldValue: (_n: string, val: Date | null) =>
              setFormField({
                ...formField,
                commission_startDate: val ? moment(val).format("DD-MM-YYYY") : "",
              }),
            setFieldTouched: () => { },
            setFieldError: () => { },
            errors: {},
            touched: {},
          }}
        />

        <Label
          required={!isFieldDisabled("commission_endDate")}
          className="settings-table-content-label"
        >
          End Date
        </Label>
        <DynamicDatePicker
          border
          name="commission_endDate"
          disabled={isFieldDisabled("commission_endDate")}
          formik={{
            values: {
              commission_endDate: formField.commission_endDate
                ? formField.commission_endDate
                : null,
            },
            setFieldValue: (_n: string, val: Date | null) =>
              setFormField({
                ...formField,
                commission_endDate: val ? moment(val).format("DD-MM-YYYY") : "",
              }),
            setFieldTouched: () => { },
            setFieldError: () => { },
            errors: {},
            touched: {},
          }}
        />

        <Label className="settings-table-content-label">
          Number of Payment
        </Label>
        <Input
          name="payment_count"
          disabled={isFieldDisabled("payment_count")}
          value={formField.payment_count || ""}
          onChange={onChange}
        />

        <Label className="settings-table-content-label">
          Show AMT on NB Register
        </Label>
        <Select
          name="nb_register"
          disabled={isFieldDisabled("nb_register")}
          value={formField.nb_register || "0"}
          onChange={onChange}
        >
          {selectFieldOptions
            .find((item: any) => item.field === "true_false")
            ?.options.map((option: any) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
        </Select>

        <Label className="settings-table-content-label">Note</Label>
        <TextEditor
          inputData={formField.commission_note}
          onChange={(text: string) =>
            setFormField({ ...formField, commission_note: text })
          }
        />
      </div>

      <div className="button-end-popup" style={{ marginTop: "20px" }}>
        {(formField.commission_status === 0 || formField.commission_status === 1 || formField.commission_status === "0" || formField.commission_status === "1") && (
          <ASCButton
            shape="rounded"
            onClick={() => props?.setStopReceiptpopup(true)}
            label="Stop Receipt"
          />
        )}
        <ASCButton
          appearance="primary"
          shape="rounded"
          className="asc-button-primary"
          onClick={handleUpdateCommission}
          disabled={loading || !isFormDirty}
          label="Save"
        />
        <ASCButton
          appearance="secondary"
          shape="rounded"
          onClick={() => props.setReceiptEditpopup(false)}
          label={"Cancel"}
        />
      </div>
    </div >
  );
};

export default CommissionForm;
